import React, { useState } from "react";
import styles from "./MilestoneRows.module.css";
import { dueTone, formatDay } from "./dueDates";
import { getInitials, avatarColorFromName } from "../../utils/avatarUtils";

export const isMilestoneDone = (m) => m.status === "completed";
export const isMilestoneLate = (m) => dueTone(m.due_date, isMilestoneDone(m)).tone === "late";

// Share of a project's milestones that are complete, 0-100.
export const milestoneProgress = (p) =>
  p.milestones?.length ? Math.round((p.milestones.filter(isMilestoneDone).length / p.milestones.length) * 100) : 0;

const STATUS_LABEL = { open: "not started", in_progress: "in progress", completed: "complete" };

// "Milestones 2/4" under a project name; opens the milestone rows. Red when one is overdue.
export function MilestoneTag({ milestones, open, onClick }) {
  if (!milestones?.length) return null;
  const done = milestones.filter(isMilestoneDone).length;
  const late = milestones.some(isMilestoneLate);
  return (
    <button
      type="button"
      className={`${styles.tag} ${late ? styles.tagLate : ""}`}
      aria-expanded={open}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      Milestones {done}/{milestones.length}
    </button>
  );
}

export function DueDate({ value, done }) {
  const { days, tone } = dueTone(value, done);
  return (
    <span className={`${styles.due} ${tone === "late" ? styles.dueLate : tone === "soon" ? styles.dueSoon : ""}`}>
      {formatDay(value)}
      {tone === "late" && <small>{-days} day{days === -1 ? "" : "s"} late</small>}
    </span>
  );
}

function JobList({ jobs, canTick, onToggleJob }) {
  return (
    <ul className={styles.jobs}>
      {jobs.map((job) => (
        <li key={job.id} className={job.done_at ? styles.jobDone : ""}>
          <input
            type="checkbox"
            checked={Boolean(job.done_at)}
            disabled={!canTick}
            onChange={() => onToggleJob(job)}
            aria-label={job.title}
          />
          <span className={styles.jobTitle}>{job.title}</span>
          {job.done_at && (
            <span className={styles.jobMeta}>
              {job.done_by_name ? `${job.done_by_name} · ` : ""}{formatDay(job.done_at)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// The milestones (and their job lists) shown under a project row. Job-list ticks only need to be on the team.
export default function MilestoneRows({ milestones, canEdit, canTickJobs, onToggleMilestone, onToggleJob, onEdit }) {
  const [openJobs, setOpenJobs] = useState({});
  return (
    <div className={styles.rows} onClick={(e) => e.stopPropagation()}>
      {milestones.map((m) => {
        const done = isMilestoneDone(m);
        const jobs = m.jobs || [];
        const jobsDone = jobs.filter((j) => j.done_at).length;
        const open = Boolean(openJobs[m.milestone_id]);
        const owner = m.assignees?.[0];
        return (
          <React.Fragment key={m.milestone_id}>
            <div className={styles.row}>
              <input
                type="checkbox"
                checked={done}
                disabled={!canEdit}
                onChange={() => onToggleMilestone(m)}
                aria-label={`Complete ${m.milestone_name}`}
              />
              <span className={`${styles.name} ${done ? styles.nameDone : ""}`}>{m.milestone_name}</span>
              {jobs.length > 0 ? (
                <button type="button" className={styles.jobTag} aria-expanded={open}
                  onClick={() => setOpenJobs((prev) => ({ ...prev, [m.milestone_id]: !open }))}>
                  Job list {jobsDone}/{jobs.length}
                </button>
              ) : canEdit && (
                <button type="button" className={`${styles.jobTag} ${styles.jobTagEmpty}`} onClick={() => onEdit(m)}>
                  + Job list
                </button>
              )}
              <span className={styles.spacer} />
              <span className={`${styles.status} ${styles[`status_${m.status || "open"}`]}`}>
                {STATUS_LABEL[m.status || "open"] || m.status}
              </span>
              <DueDate value={m.due_date} done={done} />
              <span className={styles.owner}>
                {owner && (
                  <span title={owner.user_name} style={{ background: avatarColorFromName(owner.user_name) }}>
                    {getInitials(owner.user_name)}
                  </span>
                )}
              </span>
            </div>
            {open && <JobList jobs={jobs} canTick={canTickJobs} onToggleJob={(job) => onToggleJob(job, m)} />}
          </React.Fragment>
        );
      })}
      {canEdit && (
        <button type="button" className={styles.addLink} onClick={() => onEdit(null)}>
          + Add or edit milestones
        </button>
      )}
    </div>
  );
}
